import { useState } from "react";
import { Lock } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { ASSETS } from "@/lib/site";
import { REGIONS } from "@/lib/mirrorConfig";

export function BodyMap({ onSelect }) {
  const { t, guide, setGuide } = useApp();
  const g = guide === "atez" ? "atez" : "zeta";
  const [hover, setHover] = useState(null);
  const [scanning, setScanning] = useState(null);
  const [zoom, setZoom] = useState(1);

  const charImg = g === "atez" ? ASSETS.atezChar : ASSETS.zetaChar;
  const bodyRegions = REGIONS.filter((r) => !r.discreet);
  const discreetRegions = REGIONS.filter((r) => r.discreet);

  const pick = (r) => {
    if (scanning) return;
    setScanning(r.id);
    setTimeout(() => {
      setScanning(null);
      onSelect(r);
    }, 650);
  };

  return (
    <div data-testid="body-map">
      {/* guide toggle */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {["zeta", "atez"].map((id) => (
          <button
            key={id}
            data-testid={`guide-toggle-${id}`}
            onClick={() => setGuide(id)}
            aria-pressed={g === id}
            className={`rounded-full px-5 py-2 text-xs font-bold tracking-wide border transition-colors duration-300 ${
              g === id
                ? id === "zeta"
                  ? "bg-[#1F7FC4]/20 border-[#2ECBD9] text-[#2ECBD9]"
                  : "bg-[#3FA34D]/20 border-[#A8D93B] text-[#A8D93B]"
                : "border-white/15 text-[#94A3AE] hover:text-[#F2F5F7]"
            }`}
          >
            {id.toUpperCase()}
          </button>
        ))}
        <div className="ml-3 flex items-center rounded-full border border-white/15 overflow-hidden">
          <button data-testid="zoom-out-btn" aria-label="Uzaklaştır" onClick={() => setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2)))} className="px-3 py-2 text-sm text-[#94A3AE] hover:text-[#F2F5F7]">−</button>
          <button data-testid="zoom-in-btn" aria-label="Yakınlaştır" onClick={() => setZoom((z) => Math.min(1.75, +(z + 0.25).toFixed(2)))} className="px-3 py-2 text-sm text-[#94A3AE] hover:text-[#F2F5F7]">+</button>
        </div>
      </div>

      <p className="text-center text-xs font-semibold text-[var(--accent-2)] mb-4" data-testid="guide-continue-note">
        {t({ tr: `${g.toUpperCase()} ile devam ediyorsunuz`, en: `You are continuing with ${g.toUpperCase()}` })}
      </p>

      {/* holographic figure */}
      <div className="relative overflow-hidden rounded-2xl" style={{ maxHeight: "72vh" }}>
        <div
          className="relative mx-auto transition-transform duration-300"
          style={{ width: "min(340px, 82vw)", aspectRatio: "4 / 5", transform: `scale(${zoom})`, transformOrigin: "top center" }}
        >
          {/* rotating holo rings */}
          <div className="holo-ring" aria-hidden="true" />
          <div className="holo-ring holo-ring-2" aria-hidden="true" />
          <div className="absolute inset-x-0 bottom-0 h-10 rounded-[50%] blur-xl opacity-40" style={{ background: "var(--accent)" }} aria-hidden="true" />

          <img key={g} src={charImg} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-contain holo-swap" draggable={false} />

          {/* scan effect */}
          {scanning && <div className="scan-overlay" aria-hidden="true" data-testid="scan-effect" />}

          {/* hotspots */}
          {bodyRegions.map((r) => {
            const p = r.pos[g];
            const active = hover === r.id || scanning === r.id;
            return (
              <button
                key={r.id}
                data-testid={`region-${r.id}`}
                onClick={() => pick(r)}
                onMouseEnter={() => setHover(r.id)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(r.id)}
                onBlur={() => setHover(null)}
                aria-label={t(r.label)}
                className="absolute -translate-x-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full group"
                style={{ left: `${p.x}%`, top: `${p.y}%` }}
              >
                <span className={`rounded-full hotspot-active transition-transform duration-200 flex items-center justify-center ${active ? "w-6 h-6 scale-110" : "w-[18px] h-[18px]"}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                </span>
                {(active || (r.featured && !hover && !scanning)) && (
                  <span className={`absolute whitespace-nowrap text-[11px] font-semibold px-2.5 py-1 rounded-md bg-[var(--accent)] text-white shadow-lg z-10 ${r.featured && !active ? "top-full mt-1.5 left-1/2 -translate-x-1/2" : p.x > 55 ? "right-full mr-2" : "left-full ml-2"}`}>
                    {t(r.label)}
                    {r.featured && <span className="ml-1.5 opacity-80">★</span>}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* discreet regions */}
      {discreetRegions.map((r) => (
        <button
          key={r.id}
          data-testid={`region-${r.id}`}
          onClick={() => onSelect(r)}
          className="mt-5 w-full glass-card p-4 flex items-center gap-3 text-left hover:border-[var(--accent)]/50 transition-colors"
        >
          <span className="w-10 h-10 rounded-full bg-[var(--accent-soft)] flex items-center justify-center shrink-0">
            <Lock size={16} className="text-[var(--accent-2)]" />
          </span>
          <span>
            <span className="block text-sm font-semibold text-[#F2F5F7]">{t(r.label)}</span>
            <span className="block text-xs text-[#94A3AE] mt-0.5">
              {t({ tr: "Mahremiyetiniz korunur — gizli ve güvenli ön değerlendirme", en: "Your privacy is protected — confidential, secure pre-assessment" })}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}
